/**
 * Static validator class.
 */
export default class Validator {
    /**
     * @param {object} object The object to validate.
     * @param {object} rules A set of rules to validate the object against. The
     * keys in this object should match the keys in the "object" parameter.
     * @returns {object} An object containing any validation issues. If there
     * are no issues, an empty object.
     */
    static validate(object, rules) {
        const validationIssues = {};

        for (const key in rules) {
            const rule = rules[key];

            if (
                rule.required === true &&
                Validator.validateRequired(object[key]) === false
            ) {
                validationIssues[key] = "Required";
            } else if (
                rule.type === "integer" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validateInteger(object[key]) === false
            ) {
                validationIssues[key] = "Must be an integer";
            }
            else if (
                rule.type === "number" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validateNumber(object[key], rule.min, rule.max) === false
            ) {
                validationIssues[key] = "Must be a number";
            }
            else if (
                rule.type === "phone" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validatePhone(object[key]) === false
            ) {
                validationIssues[key] = `Must be 10 digits`;
            }
            else if (
                rule.type === "zipCode" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validatePostalCode(object[key]) === false
            ) {
                validationIssues[key] = `Must be 5 digits`;
            }
            else if (
                rule.type === "npi" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validateNPI(object[key]) === false
            ) {
                validationIssues[key] = `Must be 10 digits`;
            }
            else if (
                rule.type === "mbi" &&
                object[key] !== null &&
                object[key] !== undefined &&
                object[key].trim() !== "" &&
                Validator.validateMBI(object[key]) !== true
            ) {
                validationIssues[key] = Validator.validateMBI(object[key]);
            }
            else if (
                rule.type === "email" &&
                object[key] !== null &&
                object[key] !== undefined &&
                Validator.validateEmail(object[key]) !== true
            ) {
                validationIssues[key] = Validator.validateEmail(object[key]);
            }
            else if (
                rule.type === "confirm" &&
                object[key] !== true
            ) {
                validationIssues[key] = "Required";
            }
            else if (
                rule.type === "custom"
            ) {
                const result = rule.function(object[key]);
                if (result !== true) {
                    validationIssues[key] = result;
                }
            }
        }

        return validationIssues;
    }

    /**
     * @param {*} value
     * @returns Whether or not the value is set and is not an empty string.
     */
    static validateRequired(value) {
        if (
            value === null ||
            value === undefined ||
            (
                typeof value === "string" &&
                value.trim() === ""
            )
        ) {
            return false;
        }

        return true;
    }

    /**
     * @param {*} value
     * @returns Whether or not the value is an integer.
     */
    static validateInteger(value) {
        return Number.isInteger(+value);
    }

    /**
    * @param {*} value
    * @param {number} [min] Optional minimum value.
    * @param {number} [max] Optional maximum value.
    * @returns Whether or not the value is a number.
    */
    static validateNumber(value, min = undefined, max = undefined) {
        if (value === null || value === undefined || value === '') {
            return true;
        }
        const num = Number.isInteger(+value) ? +value : Number.parseFloat(value);
        if (isNaN(num) || !isFinite(num)) {
            return false;
        }
        if (max !== undefined && num > max || max !== undefined && num < min) {
            return false;
        }
        return true;
    }

    /**
     * @param {*} value
     * @returns Whether or not the value is set and is the correct amount of
     * digits.
     */
    static validatePhone(value) {
        const trimmed = String(value).trim();
        if (trimmed === '' || trimmed === null) {
            return true;
        };
        const digits = trimmed.replace(/\D/g, '');
        return digits.length === 10;
    }

    /**
     * @param {*} value
     * @returns Whether or not the value is set and is the correct amount of
     * digits.
     */
    static validatePostalCode(value) {
        const trimmed = String(value ?? "").trim();
        if (trimmed === "") {
            return true;
        }

        if (/^\d{5}$/.test(trimmed) || /^\d{5}-\d{4}$/.test(trimmed) || /^\d{9}$/.test(trimmed)) {
            return true;
        }

        return false;
    }

    /**
     * @param {*} value
     * @returns Whether or not the value is set and is the correct amount of
     * digits.
     */
    static validateNPI(value) {
        return ('' + value).replace(/\D/g, '').length === 10;
    }

    /**
     * @param {*} value
     * @returns {string|boolean} Returns true if valid, or a specific error message if invalid.
     */
    static validateMBI(value) {
        // Check if the MBI is not empty and is exactly 11 characters
        if (value.toString().length !== 11) {
            return "MBI must be 11 characters";
        }

        // Check if the MBI contains any of the forbidden letters
        if (/S|L|O|I|B|Z/.test(value.toString().toUpperCase())) {
            return "MBI can't contain any of the following letters: S, L, O, I, B, Z";
        }

        // Check MBI format: 1[1-9][A-Z][0-9A-Z][0-9][A-Z][0-9A-Z][0-9][A-Z][A-Z][0-9][0-9]
        if (!/^[1-9][A-z][\dA-z]\d[A-z][\dA-z]\d[A-z][A-z]\d\d$/.test(value.toString())) {
            return "Invalid MBI format";
        }

        return true;
    }

    /**
     * @param {*} value
     * @returns {string|boolean} Returns true if valid, or a specific error message if invalid.
     */
    static validateEmail(value) {
        if (value.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return "Invalid email format";
        }
        return true;
    }
}
